import { lazy, Suspense } from 'react';
import { createRoute, type AnyRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { Lightbulb, Network, PenLine, Video } from 'lucide-react';
import { Spinner } from '@nw/ui';
import { ModuleGate, type WebModule } from '@nw/web-platform';
import type { MindSearch } from './MindPage.js';
import { mindCommands, mindWidgets, useMindStatus } from './assistant.js';
const forms = () => import('./forms.js');
const NewThoughtForm = lazy(() => forms().then((m) => ({ default: m.NewThoughtForm })));
const NewIdeaForm = lazy(() => forms().then((m) => ({ default: m.NewIdeaForm })));
const AddVideoForm = lazy(() => forms().then((m) => ({ default: m.AddVideoForm })));

const MindPage = lazy(() => import('./MindPage.js'));

const searchSchema = z.object({
  focus: z.string().max(200).optional(),
  root: z.string().max(200).optional(),
  depth: z.coerce.number().int().min(1).max(3).optional(),
  modules: z.string().optional(),
  types: z.string().optional(),
  predicates: z.string().optional(),
  view: z.string().optional(),
  mode: z.enum(['network', 'list']).optional(),
});

function MindRoute({ search }: { search: MindSearch }) {
  return (
    <ModuleGate moduleId="mind-networks" name="Mind Networks">
      <Suspense
        fallback={
          <div className="grid h-full place-items-center text-[var(--nw-ink)]">
            <Spinner label="Opening the network" />
          </div>
        }
      >
        <MindPage search={search} />
      </Suspense>
    </ModuleGate>
  );
}

export const mindWeb: WebModule = {
  id: 'mind-networks',
  nav: {
    label: 'Mind Networks',
    path: '/mind',
    order: 20,
    icon: Network,
    tone: 'blue',
    category: 'Thinking',
    description: 'Ideas, thoughts, videos and how everything connects',
  },
  windows: (pathname) =>
    pathname === '/mind' || pathname.startsWith('/mind/')
      ? {
          key: 'mind',
          title: 'Mind Networks',
          moduleId: 'mind-networks',
          tone: 'dark',
          surface: 'dark',
        }
      : null,
  space: { hue: 258, mode: 'dark' },
  useStatus: useMindStatus,
  commands: mindCommands,
  widgets: mindWidgets,
  routes: (parent: AnyRoute) => {
    const main = createRoute({
      getParentRoute: () => parent,
      path: '/mind',
      validateSearch: searchSchema,
      component: function Main() {
        return <MindRoute search={main.useSearch() as MindSearch} />;
      },
    });
    const entity = (segment: string, type: string) => {
      const r = createRoute({
        getParentRoute: () => parent,
        path: `/mind/${segment}/$id`,
        component: function EntityRoute() {
          const { id } = r.useParams() as { id: string };
          const key = `mind-networks:${type}:${id}`;
          return <MindRoute search={{ focus: key, root: key }} />;
        },
      });
      return r;
    };
    return [main, entity('concepts', 'concept'), entity('notes', 'note'), entity('sources', 'video')];
  },
  quickActions: [
    {
      id: 'mind.new-thought',
      label: 'Create a thought',
      description: 'Write down something you want to keep thinking about.',
      icon: PenLine,
      Component: ({ onDone }) => (
        <Suspense fallback={<Spinner />}>
          <NewThoughtForm onDone={onDone} />
        </Suspense>
      ),
    },
    {
      id: 'mind.new-idea',
      label: 'Name an idea',
      description: 'A concept you can connect books, notes and videos to.',
      icon: Lightbulb,
      Component: ({ onDone }) => (
        <Suspense fallback={<Spinner />}>
          <NewIdeaForm onDone={onDone} />
        </Suspense>
      ),
    },
    {
      id: 'mind.add-video',
      label: 'Add a video source',
      description: 'Save a YouTube talk and link it to your ideas.',
      icon: Video,
      Component: ({ onDone }) => (
        <Suspense fallback={<Spinner />}>
          <AddVideoForm onDone={onDone} />
        </Suspense>
      ),
    },
  ],
  explore: (key) => ({ to: '/mind', search: { root: key, focus: key } }),
};
